/**
 * Quarantine thresholds — one card, three numbers.
 *
 * A decision that moves the price further than any of these is not
 * pushed. The engine records it as `quarantined` and it waits in the
 * Activity log for a human to approve or discard it (Curro,
 * 2026-10-01: "bajada de precio mayor del 60 %"). Tenant-wide and not
 * a tab, on purpose: "más setup… qué pesadilla".
 *
 * Saved on blur, like the rest of Setup. Both percentages are measured
 * on the previous price; an empty field means that rule is off.
 */
import React, { useEffect, useState } from 'react'
import { Box, Stack, TextField, Typography } from '@mui/material'
import { AppCardTitle, AppNote, AppValueChip } from '@components'
import { dsColorNew } from '@palette'
import { Surface } from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import useToast from '@hooks/useToast'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'

const T = { title: 15, body: 14, label: 13, caption: 12 }

const FIELDS = [
	{ key: 'drop_pct', label: 'lowers the price more than', unit: '%' },
	{ key: 'rise_pct', label: 'raises the price more than', unit: '%' },
	{ key: 'abs_eur',  label: 'moves it more than',          unit: '€/day' },
]

export default function QuarantineCard() {
	const toast = useToast()
	const [values, setValues] = useState({ drop_pct: '', rise_pct: '', abs_eur: '' })
	const [loaded, setLoaded] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		setupApi.quarantine.get()
			.then(r => { setValues({ drop_pct: r.drop_pct ?? '', rise_pct: r.rise_pct ?? '', abs_eur: r.abs_eur ?? '' }); setLoaded(true) })
			.catch(e => setError(e?.message || 'Could not load the quarantine thresholds'))
	}, [])

	const save = async (next) => {
		try {
			const r = await setupApi.quarantine.set(
				Object.fromEntries(Object.entries(next).map(([k, v]) => [k, v === '' ? null : Number(v)])))
			setValues({ drop_pct: r.drop_pct ?? '', rise_pct: r.rise_pct ?? '', abs_eur: r.abs_eur ?? '' })
		} catch (e) {
			toast.error(e?.message || 'Could not save the thresholds')
		}
	}

	const active = FIELDS.filter(f => values[f.key] !== '' && values[f.key] != null).length

	// What the numbers mean on a 100 € price, so nobody has to do the
	// arithmetic in their head to know whether 60 is a lot.
	const n = (k) => (values[k] === '' || values[k] == null ? null : Number(values[k]))
	const example = []
	if (n('drop_pct') != null) example.push(`drop below ${(100 - n('drop_pct')).toFixed(2)} €`)
	if (n('rise_pct') != null) example.push(`rise above ${(100 + n('rise_pct')).toFixed(2)} €`)
	if (n('abs_eur') != null) example.push(`move by more than ${n('abs_eur').toFixed(2)} €/day`)

	return (
		<Surface sx={{ p: 3 }}>
			<AppCardTitle icon="shield-halved" sx={{ mb: 1.5, alignItems: 'flex-start' }}
			              iconBg={active ? dsColorNew.pillActiveBg : dsColorNew.pillTrackBg}
			              iconColor={active ? dsColorNew.appBlue : dsColorNew.textSubtle}
			              title={<Box component="span" sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
				              Quarantine
				              {loaded && (
					              <AppValueChip tone={active ? 'accent' : 'empty'}
					                            sx={{ ml: 1, py: 0.25, fontSize: 11.5, fontWeight: 600 }}>
						              {active ? `${active} rule${active > 1 ? 's' : ''} on` : 'off'}
					              </AppValueChip>
				              )}
			              </Box>}
			              subtitle={<Box component="span" sx={{ display: 'block', lineHeight: 1.45 }}>
				              Hold a decision for approval in the Activity log, instead of pushing it,
				              when it… Leave a field empty to switch that rule off. In simulation the hold
				              is recorded too, so you can calibrate before going live.
			              </Box>}/>
			{error && <AppNote severity="error" sx={{ mb: 1.5 }}>{error}</AppNote>}
			<Stack gap={1.5} sx={{ mt: 1 }}>
				{FIELDS.map(f => (
					<Stack key={f.key} direction="row" alignItems="center" gap={1}>
						<Typography sx={{ fontSize: T.label, color: dsColorNew.textDark, whiteSpace: 'nowrap' }}>
							{f.label}
						</Typography>
						<TextField size="small" type="number" disabled={!loaded} sx={inputSx}
						           value={values[f.key]}
						           onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
						           onBlur={() => save(values)}
						           inputProps={{ min: 0, step: '0.5',
						                         'aria-label': `Quarantine when the decision ${f.label} (${f.unit})`,
						                         style: { width: 64, textAlign: 'right', fontWeight: 600, fontSize: T.body } }}/>
						<Typography sx={{ fontSize: T.label, fontWeight: 500, color: dsColorNew.textMuted }}>{f.unit}</Typography>
					</Stack>
				))}
			</Stack>
			<Typography sx={{ fontSize: T.label, color: dsColorNew.textMuted, mt: 2.5, lineHeight: 1.5 }}>
				{example.length
					? <>On a 100 € price, a decision is held when it would {example.join(', or ')}. Anything milder goes through.</>
					: <>No rule is on: every decision goes through without a human looking at it.</>}
			</Typography>
		</Surface>
	)
}
